import Backbone from 'backbone'
import template from './object-selector.hbs'
import OrgGardener from 'scoreboard/organizations/organizations-gardener'
import globals from 'scoreboard/components/globals'
import _ from 'lodash'
import metadataGardeners from 'scoreboard/components/metadata-gardeners'
import NoObjectView from 'scoreboard/components/no-object'
import HttpStatusCodes from 'http-status-codes'
import noPermission from 'scoreboard/components/no-permission.hbs'
import EmptyState from './empty-state'
import projects from 'scoreboard/projects/projects'
import notFound from 'scoreboard/components/not-found.hbs'
import metadata from 'scoreboard/components/metadata'
import i18n from 'scoreboard/components/i18n'
import ExpandCollapse from 'scoreboard/components/object-selector-expand-collapse'
import spectator from 'scoreboard/components/spectator'
import unwrapModel from 'scoreboard/components/unwrap-model'

/**
 * Allows selection of domain objects such as scorecard nodes by displaying an
 * org tree and domain object tree (e.g. scorecard tree). Example usage:
 *
 * <pre>
 * this.objectSelector = new ObjectSelector({
 * 	   section: 'scorecards',
 * })
 * this.$('.object-selector').replaceWith(this.objectSelector.render().el)
 * this.objectSelector.on('select', function(node) {})
 * this.objectSelector.on('selectOrg', function(node) {})
 * this.objectSelector.on('success', function(node) {})
 * this.objectSelector.on('empty', function() {})
 * var object = this.objectSelector.selected()
 * var org = this.objectSelector.selectedOrg()
 * </pre>
 */
var ObjectSelector = Backbone.View.extend({

	className: 'object-selector',

	_template: template,

	/** @type OrgGardener */
	orgGardener: null,
	/** @type Gardener */
	objectGardener: null,

	/**
	 * @constructs
	 *
	 * @param {Object} options all options are also passed through to the object gardener
	 *
	 * @param {Object} [options.objectGardener] provide a custom gardener
	 *
	 * @param {String} options.section indicates the type of object we're
	 * selecting from the tree on the right
	 *
	 * @param {Number} [options.id] the ID of an object to show pre-selected
	 *
	 * @param {Number} [options.orgId] the ID of an organization to show pre-selected
	 *
	 * @param {Number} [options.projectId] Project Id passed to Gardener
	 *
	 * @param {Object} [options.orgParams] extra parameters to append to the URL
	 * used by organization gardener
	 *
	 * @param {Object} [options.objectParams] extra parameters to append to the
	 * URL used by domain object gardener
	 *
	 * @param {Boolean} [options.showProjectSelector=false] Pass true go have a
	 * project drop down displayed in the org selector.
	 */
	initialize: function(options = {}) {
		this.options = options

		//#23291 handle org-less/project-less spectated objects
		const {orgId, projectId} = _.get(spectator.getCurrent(), 'attributes' , {})
		if (!_.isNil(orgId) && !_.isNil(projectId)) {
			this.options.orgId = orgId ||
				_.get(projects.get(projectId), 'attributes.orgId') ||
				globals.orgId // #23285
			this.options.projectId = projectId
		} // Otherwise, set the org based on the selected project/org
		else if (!this.options.id && !this.options.orgId) {
			// #27496 If the user hasn't selected a project yet (e.g. they navigated
			// directly to a section that is orgless, like a dataset) and they
			// launch an object selector we need a project/org to render it.  So
			// here we just pick the first project (without selecting it as the
			// active project).
			this.selectProject(projects.selectedOrFirstId())
		}

		this.section = options.section || this.section
	},

	render: function() {
		this.$el.empty().append(this._template)
		this.renderGardeners()
		return this
	},

	renderGardeners() {
		this._renderOrgGardener()
		this._renderObjectGardener()

		if (!this.options.preventStandardExpand) {
			this.expandCollapse = new ExpandCollapse({
				el: this.$el,
				gardeners: [this.orgGardener, this.objectGardener],
				types: ['org', 'object'],
				unitType: '%'
			}).render()
		}
	},

	selectProject(projectId) {
		let projectOrgId = _.get(unwrapModel(projects.get(projectId)), 'orgId')
		if (!this.options.projectId || this.options.projectId == projectId) {
			this.options.orgId = globals.orgId || projectOrgId
		} else {
			this.options.orgId = projectOrgId || globals.orgId
		}
		this.options.projectId = projectId
	},

	/**
	 * Get the state of the object selector.
	 *
	 * projectId is the id for the project that is selected (if projects are enabled).
	 * organizationId is the id for the selected org (if one is selected).
	 * id is the id for the actual object that's selected (if there is one).
	 *
	 * @return {{projectId, organizationId , id}}
	 */
	getSelectedState() {
		let selectedOrg = _.invoke(this.orgGardener, 'tree.selected')
		let selectedObj = _.invoke(this.objectGardener, 'tree.selected')
		return {
			projectId: _.invoke(this.orgGardener, 'getProjectId'),
			organizationId: _.get(selectedOrg, 'id'),
			id: _.get(selectedObj, 'id')
		}
	},

	_renderOrgGardener() {
		_.invoke(this.orgGardener, 'remove')

		// show the org tree first b/c it looks weird to show the object
		// tree first
		this.orgGardener = new OrgGardener({
			id: this.options.orgId,
			section: this.section,
			permissions: this.options.permissions,
			params: this.options.orgParams,
			finderOptions: {
				type: 'light'
			},
			projectId: this.options.projectId,
			showProjectSelector: this.options.showProjectSelector,
			streamOptions: {
				projectId: this.options.projectId // #31131
			}
		})

		this.$('.org').append(this.orgGardener.el)
		this.orgGardener.render()
		this.$('.org .tree-container').addClass('tree-container-light')
		this.orgGardener.on('select', this._onOrgSelect.bind(this))
		this.orgGardener.on('projectId', projectId => {
			// We're changing the project, the tree is loading the new org/object
			this.trigger('toggle:loading', true)
			this._toggleTreeRendered()

			const orgId = projects.get(projectId).get('orgId')
			this.orgGardener.show({
				projectId,
				id: orgId
			})
			// Select the root org so the object selector is updated for the new project
			if (orgId) {
				this._onOrgSelect({id: orgId})
				this.$('.org .tree-container').addClass('tree-container-light')
			} else {
				this.orgGardener.displayNoOrgs()
				this.objectGardener.finder.clear()
				this.$('.object').hide()
			}
		})

		this.orgGardener.on('tree:rendered', () => {
			this._toggleTreeRendered('org')
			this.trigger('org:tree:rendered')
		})
	},

	_renderObjectGardener: function(options) {
		_.invoke(this.objectGardener, 'remove')

		var gardener = this.ObjectGardener ||
			this.options.objectGardener ||
			metadataGardeners[this.section]

		this.objectGardener = new gardener(_.extend({
			section: this.section,
			toggleOnSelect: false,
			indicator: false,
			params: this.options.objectParams,
			finderOptions: {
				type: 'light'
			}
		}, this.options, options))
		this.$('.object').append(this.objectGardener.el)
		this.objectGardener.render()

		this.objectGardener.on('select', this._onObjectSelect.bind(this))
		this.objectGardener.on('node', this._onObjectSuccess.bind(this))
		this.objectGardener.on('error', this._onObjectError.bind(this))
		this.objectGardener.on('end', this.trigger.bind(this, 'end'))
		this.objectGardener.on('tree:rendered', ()  => this._toggleTreeRendered('object'))
		this.objectGardener.on('projectId', (projectId) => {
			this.objectGardener.show({ projectId })
		})
	},

	// Keeps track of which trees are loaded, when both trees are loaded, raise
	// a trigger. When no param is passed in, reset the flags.
	_toggleTreeRendered(tree) {
		if (_.isNil(tree)) {
			this.orgTreeRendered = false
			this.objectTreeRendered = false
			_.invokeMap([this.orgGardener, this.objectGardener], 'insertLoadingDots')
			return
		}

		if (tree == 'org') {
			this.orgTreeRendered = true
		}

		if (tree == 'object') {
			this.objectTreeRendered = true
		}

		// Both trees are loaded, trigger the toggle:loading event
		if (this.orgTreeRendered && this.objectTreeRendered) {
			_.invokeMap([this.orgGardener, this.objectGardener], 'clearLoadingDots')
			this.trigger('toggle:loading', false)
		}
	},

	treesRendered() {
		return this.orgTreeRendered && this.objectTreeRendered
	},

	_onFind: function(gardener, term) {
		// issue ##9908 only search (or clear search) if the root has an element
		if (!_.isEmpty(gardener.tree.root)) {
			gardener.tree.search(term)
			this.trigger('search')
		}
	},

	// when data is found in the organization
	_onObjectSuccess: function(node) {
		// #7781 needed if we started with an empty org but then added something
		this.$('.object .tree-container .tree').removeClass('is-not-displayed')
		this.$('.object .tree-container').addClass('tree-container-light')
		// issue ##9908 remove empty state div if present
		this.clearEmptyState()

		// make sure the correct org is selected if necessary
		if (node.organizationId && !this.orgGardener.tree.selected()) {
			this.orgGardener.select(node.organizationId, {
				silent: true,
				toggleOnSelect: false,
				animate: false
			})
		}

		//update the replication status from the header returned
		this._updateReplicationStatus()

		// let listeners know we found an org with data
		this.trigger.apply(this, _.flatten(['success'].concat(arguments)))
	},

	// when no data is found in the organization
	_onObjectError: function() {
		this.$('.object .tree-container').find('.tree, .loading-dots')
			.addClass('is-not-displayed')
		var view = new NoObjectView({
			section: this.section,
			noHeader: true,
			// If section title should be overridden.
			// i.e. if "Charts" should be shown instead of "Reports". Issue #19244
			sectionSubstitution: this.options.sectionSubstitution
		})

		//in case the replication status has changed, update it with the latest info from the server
		// #31389 if we are offline, there will be no headers
		if (this.objectGardener.stream.headers) {
			this._updateReplicationStatus()
		}

		let status = this.objectGardener.stream.statusCode
		let data = this.objectGardener.stream.responseJSON
		if (status == HttpStatusCodes.NOT_FOUND) {
			if (data.dereplicated) {
				let message = i18n.get('common.replication.itemMayExistOrgNotReplicated', metadata[this.section].singularCode)
				view = new EmptyState({
					body: notFound({message}),
					noHeader: true
				})
			}
		} else if (status == HttpStatusCodes.FORBIDDEN) {
			let message = null
			let hideContactAdminMessage = false
			if (!_.isNil(data.mixedReplicationDereplicatedId)) {
				message = data.message
				hideContactAdminMessage = true
			}
			view = new EmptyState({
				body: noPermission({message, hideContactAdminMessage}),
				noHeader: true
			})
		}

		this.$('.object .tree-container').append(view.el)
		view.render()
		// let listeners know there is no object tree (org is empty/no permissions/not replicated)
		this.trigger('empty', view)
	},

	_onOrgSelect: function(node) {
		this.$('.object').show()
		if (!this.objectGardener) {
			this.objectGardener.show({ orgId: node.id })
		} else {
			this.objectGardener.show({
				orgId: node.id,
				section: this.section,
				toggleOnSelect: false
			})
		}
		this.orgGardener.finder.clear()
		this.trigger.apply(this, _.flatten(['selectOrg'].concat(arguments)))
	},

	_onObjectSelect: function() {
		this.objectGardener.finder.clear()
		this.trigger.apply(this, _.flatten(['select'].concat(arguments)))
	},

	select: function(id) {
		this.objectGardener.tree.select(id)
	},

	selectOrg: function(id) {
		this.orgGardener.tree.select(id)
	},

	/**
	 * Returns domain object selected in the right pane (e.g. dashboard)
	 */
	selected: function () {
		if (!this.objectGardener) {
			return null
		}
		let selected = _.result(this.objectGardener, 'tree.selected')
		if (selected) {
			selected = _.extend({}, selected, {
				projectId: _.invoke(this.orgGardener, 'getProjectId')
			})
		}
		return selected
	},

	/**
	 * Returns the selected organization
	 */
	selectedOrg: function () {
		return this.orgGardener.tree.selected()
	},

	stopListening: function() {
		Backbone.View.prototype.stopListening.apply(this, arguments)

		_.invokeMap([this.orgGardener, this.objectGardener], 'stopListening')
	},

	// resize trees associated with this object selector
	resize: function() {
		this.orgGardener && _.invoke(this.orgGardener.tree, 'resize')
		this.objectGardener && _.invoke(this.objectGardener.tree, 'resize')
	},

	_updateReplicationStatus () {
		let repStatusHeader = _.get(this.objectGardener, 'stream.headers["organization-replication-status"]', null)
		if (!_.isNil(repStatusHeader)) {
			this.orgGardener._onceTreeLoaded(() => {
				let org = this.orgGardener.tree.get(this.objectGardener.options.orgId)
				org.replicated = repStatusHeader == "true"
			})
		}
	},

	/**
	 * Removes the empty state div if present
	 */
	clearEmptyState: function() {
		this.$('.no-object').remove()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)

		_.invokeMap([this.orgGardener, this.objectGardener, this.expandCollapse], 'remove')
	}

})

export default ObjectSelector

ObjectSelector.__filename__ = 'components/object-selector.js'
